<template>
  <v-container>
    <v-card class="elevation-2 pa-4 lease-card">
      <v-card-title class="headline primary--text">
        Edit Lease: {{ leaseReference || '...' }}
      </v-card-title>
      <v-divider class="mb-4"></v-divider>

      <!-- Loading overlay -->
      <v-overlay
        :value="isLoading"
        absolute
        color="white"
        opacity="0.65"
      >
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 text-body-1 primary--text font-weight-medium">
          Loading lease details...
        </div>
      </v-overlay>

      <div v-if="!isLoading">
        <!-- Property Selection -->
        <v-card-text>
          <h3 class="subtitle-1 mb-2">Property Information</h3>
          <property-selector
            :properties="propertyList"
            :selected-property="selectedProperty"
            @property-selected="updateSelectedProperty"
          />
          <v-text-field
            v-model="address"
            label="Unit/Address"
            outlined
            dense
            class="mt-2"
            readonly
          ></v-text-field>
        </v-card-text>

        <!-- Tenant Information -->
        <v-card-text>
          <h3 class="subtitle-1 mb-2">Tenant Information</h3>
          <v-select
            :items="tenants"
            item-text="displayName"
            item-value="id"
            label="Select Tenant"
            v-model="selectedTenant"
            :loading="isLoadingTenants"
            outlined
            dense
            :rules="[v => !!(v && v.id) || 'Tenant selection is required']"
            return-object
          ></v-select>
        </v-card-text>

        <!-- Deposit Details -->
        <v-card-text>
          <h3 class="subtitle-1 mb-2">Deposit Details</h3>
          <v-text-field
            v-model="depositDetails.depositRequired"
            label="Deposit Required (R)"
            type="number"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="depositDetails.depositHeld"
            label="Deposit Held (R)"
            type="number"
            outlined
            dense
          ></v-text-field>
        </v-card-text>

        <!-- Lease Details -->
        <v-card-text>
          <h3 class="subtitle-1 mb-2">Lease Details</h3>
          <v-form @submit.prevent="updateLease">
            <v-select
              v-model="leaseDetails.type"
              :items="['Fixed Term Lease', 'Month to Month Lease']"
              label="Lease Type"
              outlined
              dense
              required
              class="form-field"
            ></v-select>

            <v-text-field
              v-model="leaseDetails.startDate"
              label="Start Date"
              type="date"
              outlined
              dense
              required
              class="form-field"
            ></v-text-field>

            <v-text-field
              v-model="leaseDetails.endDate"
              label="End Date"
              type="date"
              outlined
              dense
              required
              class="form-field"
            ></v-text-field>

            <v-select
              v-model="leaseDetails.rentDueFrequency"
              :items="rentDueFrequencyOptions"
              label="Rent Due Frequency"
              outlined
              dense
              required
              class="form-field"
            ></v-select>

            <v-select
              v-model="leaseDetails.dueDay"
              :items="['1st of each Month']"
              label="Due Day"
              outlined
              dense
              required
              class="form-field"
            ></v-select>

            <v-text-field
              v-model="leaseDetails.rentalAmount"
              label="Rental Amount (R)"
              outlined
              dense
              required
              class="form-field"
              :rules="[value => !!value || 'Required', value => value > 0 || 'Must be positive']"
            ></v-text-field>

            <v-text-field
              v-model="leaseDetails.invoiceDate"
              label="Invoice Date"
              type="date"
              outlined
              dense
              required
              class="form-field"
            ></v-text-field>

            <!-- Prorated Amount – now optional -->
            <v-text-field
              v-model="leaseDetails.prorataAmount"
              label="Prorated Amount (R)"
              outlined
              dense
              class="form-field"
              hint="Optional – leave blank if not applicable"
              persistent-hint
            ></v-text-field>

            <v-btn
              type="submit"
              color="primary"
              class="mt-4 submit-button"
              :loading="isSaving"
              :disabled="!isFormValid || isSaving"
            >
              Update Lease
            </v-btn>
          </v-form>
        </v-card-text>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import { getDocs, collection, getFirestore, where, query, doc, updateDoc } from "firebase/firestore";
import PropertySelector from "./PropertySelection.vue";
import { getAuth } from "firebase/auth";

const db = getFirestore();
const auth = getAuth();

export default {
  components: {
    PropertySelector
  },

  data() {
    return {
      isLoading: true,
      isSaving: false,
      isLoadingTenants: false,

      propertyList: [],
      tenants: [],
      selectedProperty: null,
      selectedTenant: null,

      leaseId: '',
      leaseReference: '',
      propertyId: '',
      propertyName: '',
      tenantId: '',
      address: '',
      userUid: '',
      createdAt: '',

      depositDetails: {
        depositRequired: '',
        depositHeld: ''
      },

      leaseDetails: {
        type: '',
        startDate: '',
        endDate: '',
        rentDueFrequency: '',
        dueDay: '',
        rentalAmount: '',
        invoiceDate: '',
        prorataAmount: ''
      },

      rentDueFrequencyOptions: [
        '1st of each Month',
        'Weekly',
        'Yearly',
        'Daily',
        'Every two weeks',
        'Every two months',
        'Every three months (Quarterly)',
        'Every four months',
        'Every five months',
        'Every six months',
        'Every seven months',
        'Every eight months',
        'Every nine months',
        'Every ten months',
        'Every eleven months'
      ]
    };
  },

  computed: {
    isFormValid() {
      return (
        !!this.selectedProperty &&
        !!this.selectedTenant?.id &&
        !!this.leaseDetails.type &&
        !!this.leaseDetails.startDate &&
        !!this.leaseDetails.endDate &&
        !!this.leaseDetails.rentDueFrequency &&
        !!this.leaseDetails.dueDay &&
        !!this.leaseDetails.rentalAmount &&
        Number(this.leaseDetails.rentalAmount) > 0 &&
        !!this.leaseDetails.invoiceDate
      );
    }
  },

  async created() {
    this.isLoading = true;
    try {
      // 1. Load properties first (needed for pre-selecting)
      await this.fetchProperties();

      // 2. Load tenants in parallel with lease (but properties must be ready)
      await Promise.all([
        this.fetchTenants(),
        this.fetchLeaseByReference()
      ]);

      // 3. Now that propertyList is populated, set the selected property
      if (this.propertyId) {
        this.selectedProperty = this.propertyList.find(p => p.id === this.propertyId) || null;
      }

      // Tenant can be set here too (safer)
      if (this.tenantId) {
        this.selectedTenant = this.tenants.find(t => t.id === this.tenantId) || null;
      }
    } catch (err) {
      console.error("Initial data load failed:", err);
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    async fetchProperties() {
      try {
        const querySnapshot = await getDocs(collection(db, "properties"));
        const properties = await Promise.all(querySnapshot.docs.map(async (doc) => {
          const data = doc.data();
          const propertyId = doc.id;

          const unitsQuerySnapshot = await getDocs(collection(db, "properties", propertyId, "units"));
          const units = unitsQuerySnapshot.docs.map((unitDoc) => {
            const unitData = unitDoc.data();
            return {
              id: unitDoc.id,
              unitName: unitData.unitName,
              isOccupied: unitData.isOccupied,
            };
          });

          return {
            id: propertyId,
            displayName: data.displayName,
            address: data.address,
            units,
            isVacant: data.isVacant,
          };
        }));

        properties.sort((a, b) => (a.displayName < b.displayName ? -1 : 1));
        this.propertyList = properties;
      } catch (error) {
        console.error("Error fetching properties:", error);
        this.$toast?.error('Failed to fetch properties');
      }
    },

    async fetchTenants() {
      try {
        this.isLoadingTenants = true;
        const q = query(collection(db, "tenants"));
        const querySnapshot = await getDocs(q);

        this.tenants = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: data.reference,
            displayName: data.displayAs || `${data.person?.firstName || ''} ${data.person?.surname || ''}`,
            ...data
          };
        }).sort((a, b) => a.displayAs.localeCompare(b.displayAs));
      } catch (error) {
        console.error("Error fetching tenants:", error);
        this.$toast?.error('Failed to fetch tenants');
      } finally {
        this.isLoadingTenants = false;
      }
    },

    async fetchLeaseByReference() {
      try {
        this.leaseReference = this.$route.params.id;

        if (!this.leaseReference) {
          this.$toast?.error("No lease reference provided in URL");
          return;
        }

        const leaseQuery = query(
          collection(db, "Leases"),
          where("leaseReference", "==", this.leaseReference)
        );

        const leaseSnapshot = await getDocs(leaseQuery);

        if (leaseSnapshot.empty) {
          this.$toast?.error('Lease not found');
          return;
        }

        const leaseDoc = leaseSnapshot.docs[0];
        const leaseData = leaseDoc.data();

        this.leaseId = leaseDoc.id;
        this.propertyId = leaseData.propertyId || '';
        this.tenantId = leaseData.tenantId || '';
        this.address = leaseData.address || '';
        this.createdAt = leaseData.createdAt || '';
        this.userUid = leaseData.userUid || '';
        this.propertyName = leaseData.propertyName || '';

        this.depositDetails = {
          depositRequired: leaseData.depositRequired || '',
          depositHeld: leaseData.depositHeld || ''
        };

        if (leaseData.leaseDetails) {
          this.leaseDetails = {
            type: leaseData.leaseDetails.type || '',
            startDate: leaseData.leaseDetails.startDate || '',
            endDate: leaseData.leaseDetails.endDate || '',
            rentDueFrequency: leaseData.leaseDetails.rentDueFrequency || '',
            dueDay: leaseData.leaseDetails.dueDay || '1st of each Month',
            rentalAmount: leaseData.leaseDetails.rentalAmount || '',
            invoiceDate: leaseData.leaseDetails.invoiceDate || '',
            prorataAmount: leaseData.leaseDetails.prorataAmount || ''
          };
        }
      } catch (error) {
        console.error("Error fetching lease details:", error);
        this.$toast?.error('Failed to fetch lease details');
      }
    },

    updateSelectedProperty(property) {
      this.selectedProperty = property;
      this.propertyId = property.id;
      this.propertyName = property.displayName;
      this.address = property.units?.length > 0
        ? property.units[0].unitName
        : property.address || '';
    },

    async updateLease() {
      try {
        this.isSaving = true;

        if (!this.selectedTenant || !this.selectedTenant.id) {
          throw new Error("No tenant selected");
        }

        const updatedData = {
          address: this.address,
          depositHeld: this.depositDetails.depositHeld,
          depositRequired: this.depositDetails.depositRequired,
          leaseDetails: this.leaseDetails,
          leaseReference: this.leaseReference,
          propertyId: this.propertyId,
          propertyName: this.propertyName,
          tenantId: this.selectedTenant.id,
          userUid: this.userUid,
          updatedAt: new Date().toISOString()
        };

        await updateDoc(doc(db, "Leases", this.leaseId), updatedData);

        this.$router.push({ path: "/lease", query: { type: "lease" } });
      } catch (error) {
        console.error("Error updating lease:", error);
        this.$toast?.error(error.message || 'Failed to update lease');
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.lease-card {
  max-width: 800px;
  margin: 20px auto;
  background-color: #f9f9f9;
  border-radius: 12px;
}

.primary--text {
  color: #1976d2;
  font-weight: bold;
  font-size: 20px;
}

.form-field {
  margin-bottom: 16px;
}

.submit-button {
  width: 100%;
  color: white;
  font-weight: bold;
}

.subtitle-1 {
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
}
</style>